import { useCallback, useEffect, useState } from 'react';
import type { Objective, ObjectiveSignals, UpsertObjectiveInput } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

const EMPTY_SIGNALS: ObjectiveSignals = {
  annotatedCount: 0,
  readyModelCount: 0,
  evaluatedModelCount: 0,
};

/**
 * Objectif d'un projet + ses signaux (ADR-0028). L'objectif (l'intention) est *stocké* ; les signaux
 * sont *dérivés* des données réelles — le plan se calcule ensuite côté composant via la fonction
 * partagée `deriveObjectivePlan`, jamais persisté. `save`/`clear` rafraîchissent l'ensemble.
 */
export function useObjective(projectId: string) {
  const [objective, setObjective] = useState<Objective | null>(null);
  const [signals, setSignals] = useState<ObjectiveSignals>(EMPTY_SIGNALS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [obj, sig] = await Promise.all([
        apiFetch<Objective | null>(`/projects/${projectId}/objective`),
        apiFetch<ObjectiveSignals>(`/projects/${projectId}/objective/signals`),
      ]);
      setObjective(obj);
      setSignals(sig ?? EMPTY_SIGNALS);
    } catch {
      // Projet étranger ou rôle sans accès : on laisse l'objectif nul, sans écran d'erreur brutal.
      setObjective(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (input: UpsertObjectiveInput) => {
      await apiFetch(`/projects/${projectId}/objective`, { method: 'PUT', body: input });
      await refresh();
    },
    [projectId, refresh],
  );

  const clear = useCallback(async () => {
    await apiFetch(`/projects/${projectId}/objective`, { method: 'DELETE' });
    await refresh();
  }, [projectId, refresh]);

  return { objective, signals, loading, save, clear };
}
